/**
 * Hook for enhanced Argentina province detection
 * Uses multiple detection methods including Google Analytics
 */

import { detectArgentinaProvinceEnhanced, getProvinceName } from "@/lib/argentinaProvinceDetection";
import { useEffect, useState } from "react";

interface ProvinceState {
  province: string | null;
  provinceName: string | null;
  isLoading: boolean;
  error: string | null;
  detectionMethod: string | null;
}

export function useArgentinaProvince(country?: string | null) {
  const [state, setState] = useState<ProvinceState>({
    province: null,
    provinceName: null,
    isLoading: false,
    error: null,
    detectionMethod: null
  });

  useEffect(() => {
    // Only detect if country is Argentina
    if (country !== "AR") {
      setState({
        province: null,
        provinceName: null,
        isLoading: false,
        error: null,
        detectionMethod: null
      });
      return;
    }

    let isMounted = true;

    const detectProvince = async () => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const detectedProvince = await detectArgentinaProvinceEnhanced();
        
        if (isMounted) {
          const provinceName = detectedProvince ? getProvinceName(detectedProvince) : null;
          
          setState({
            province: detectedProvince,
            provinceName,
            isLoading: false,
            error: null,
            detectionMethod: detectedProvince ? "enhanced_detection" : "no_detection",
          });
          if (import.meta.env.DEV) {
            console.log("Argentina province (enhanced)", { province: detectedProvince, provinceName, method: "enhanced_detection" });
          }
        }
      } catch (error) {
        
        if (isMounted) {
          setState({
            province: 'BA', // Fallback to Buenos Aires
            provinceName: 'Buenos Aires',
            isLoading: false,
            error: error instanceof Error ? error.message : 'Detection failed',
            detectionMethod: 'fallback'
          });
        }
      }
    };

    detectProvince();

    return () => {
      isMounted = false;
    };
  }, [country]);

  return state;
}

/**
 * Hook for simple synchronous province detection (for immediate use)
 */
export function useArgentinaProvinceSync(country?: string | null) {
  const [province, setProvince] = useState<string | null>(null);
  const [provinceName, setProvinceName] = useState<string | null>(null);

  useEffect(() => {
    if (country !== "AR") {
      setProvince(null);
      setProvinceName(null);
      return;
    }

    // Simple timezone-based detection
    if (typeof window !== "undefined") {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let detectedProvince = null;

      if (timezone === "America/Argentina/Buenos_Aires") detectedProvince = "BA";
      else if (timezone === "America/Argentina/Cordoba") detectedProvince = "CB";
      else if (timezone === "America/Argentina/Mendoza") detectedProvince = "MZ";
      else if (timezone === "America/Argentina/Tucuman") detectedProvince = "TM";
      else if (timezone === "America/Argentina/Salta") detectedProvince = "SA";
      else if (timezone === "America/Argentina/Jujuy") detectedProvince = "JY";
      else if (timezone.includes("Argentina")) detectedProvince = "BA"; // Default fallback

      setProvince(detectedProvince);
      setProvinceName(detectedProvince ? getProvinceName(detectedProvince) : null);
      if (import.meta.env.DEV) {
        console.log("Argentina province (sync)", {
          timezone,
          province: detectedProvince,
          provinceName: detectedProvince ? getProvinceName(detectedProvince) : null,
        });
      }
    }
  }, [country]);

  return {
    province,
    provinceName,
    isLoading: false,
    error: null,
    detectionMethod: province ? 'timezone_sync' : 'no_detection'
  };
}

export default {
  useArgentinaProvince,
  useArgentinaProvinceSync
};